import { OptionsApi } from '@core/swagger-client';
import { SubtitlesTrack } from '@core/types';
import {
    Button,
    CompositePageHeader,
    ContainerNoTopSpace,
    Delete,
    Download,
    ErrorMessage,
    ErrorMessagePage,
    Heading,
    LoaderDots,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Upload,
    useFileDropzone
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { parse } from '@plussub/srt-vtt-parser';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { GenerateSubtitlesOptionsOrganism } from '../../organisms';

export const EditSubtitlesPage = () => {
    const { webinarId } = useParams({ strict: false });
    const { t } = useTranslation();
    const { data: webinar, isLoading: isWebinarLoading, error: webinarError } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));
    const { data: generatorStatus, refetch: refetchGeneratorStatus } = useQuery(OptionsApi.subtitleGenerator.getStatus(webinarId!, { refetchInterval: 1000 }));
    const [showSubtitlesGeneratorOptions, setShowSubtitlesGeneratorOptions] = useState(false);
    const [wasGenerating, setWasGenerating] = useState(false);

    useEffect(() => {
        if (generatorStatus?.isGenerating) {
            setWasGenerating(true);
        }

        if (wasGenerating && !generatorStatus?.isGenerating) {
            // Generation just finished
            refetchTracks();
            setWasGenerating(false);
        }
    }, [wasGenerating, generatorStatus?.isGenerating]);

    const addOrUpdateTrackMutation = useMutation(OptionsApi.subtitles.addOrUpdateTrack());
    const { data: tracks, isLoading: isTracksLoading, error: tracksError, refetch: refetchTracks } = useQuery(OptionsApi.subtitles.getTracks(webinarId!));

    const { getRootProps, getInputProps, isDragActive, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length === 0) {
                return;
            }

            const file = acceptedFiles[0];
            const reader = new FileReader();
            reader.onload = () => {
                // Parse VTT/SRT and upload
                const content = reader.result as string;
                if (!content) return;

                const subtitles = parse(content);
                const trackId = file.name.replace(/\.[^/.]+$/, ''); // Remove file extension for track ID

                const track: SubtitlesTrack = {
                    trackId,
                    isDefault: false,
                    isEnabled: true,
                    subtitles: subtitles.entries.map((cue) => ({
                        start: Duration.fromMillis(cue.from).toISOTime(),
                        end: Duration.fromMillis(cue.to).toISOTime(),
                        lines: cue.text.split('\n')
                    }))
                };
                addOrUpdateTrackMutation.mutate(
                    { webinarId: webinarId!, track },
                    {
                        onSuccess: () => {
                            refetchTracks();
                        }
                    }
                );
            };
            reader.readAsText(file);
        },
        accept: {
            'text/vtt': ['.vtt'],
            'application/x-subrip': ['.srt']
        }
    });

    const toggleTrackEnable = (trackId: string) => {
        const track = tracks?.find((t) => t.trackId === trackId);
        if (!track) return;
        const updatedTrack: SubtitlesTrack = {
            ...track,
            isEnabled: !track.isEnabled
        };
        if (!updatedTrack.isEnabled) {
            updatedTrack.isDefault = false;
        }
        addOrUpdateTrackMutation.mutate(
            { webinarId: webinarId!, track: updatedTrack },
            {
                onSuccess: () => {
                    refetchTracks();
                }
            }
        );
    };

    const toggleTrackDefault = (trackId: string) => {
        const track = tracks?.find((t) => t.trackId === trackId);
        if (!track) return;

        const defaultTracks = tracks?.filter((t) => t.isDefault) || [];
        for (const defTrack of defaultTracks) {
            if (defTrack.trackId !== trackId) {
                const updatedDefTrack: SubtitlesTrack = {
                    ...defTrack,
                    isDefault: false
                };
                addOrUpdateTrackMutation.mutate(
                    { webinarId: webinarId!, track: updatedDefTrack },
                    {
                        onSuccess: () => {
                            refetchTracks();
                        }
                    }
                );
            }
        }

        const updatedTrack: SubtitlesTrack = {
            ...track,
            isDefault: !track.isDefault
        };
        addOrUpdateTrackMutation.mutate(
            { webinarId: webinarId!, track: updatedTrack },
            {
                onSuccess: () => {
                    refetchTracks();
                }
            }
        );
    };

    const onDownloadTrack = (trackId: string) => {
        const track = tracks?.find((t) => t.trackId === trackId);
        if (!track) return;

        let index = 1;
        let vtt = 'WEBVTT\n\n';
        for (const subtitle of track.subtitles) {
            vtt += `${index++}\n`;
            // Default TimeSpan format for .NET includes microseconds, but VTT requires milliseconds
            // Converting via Duration to ensure correct formatting
            vtt += `${Duration.fromISOTime(subtitle.start).toISOTime()} --> ${Duration.fromISOTime(subtitle.end).toISOTime()}\n`;
            for (const line of subtitle.lines) {
                vtt += `${line}\n`;
            }
            vtt += '\n';
        }

        const blob = new Blob([vtt], { type: 'text/vtt' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${track.trackId}.vtt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const deleteTrackMutation = useMutation(OptionsApi.subtitles.deleteTrack());
    const onDeleteTrack = (trackId: string) => {
        deleteTrackMutation.mutate(
            { webinarId: webinarId!, trackId },
            {
                onSuccess: () => {
                    refetchTracks();
                }
            }
        );
    };

    const closeSubtitlesGeneratorOptions = () => {
        setShowSubtitlesGeneratorOptions(false);
        refetchGeneratorStatus();
    };

    const error = webinarError ?? tracksError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isWebinarLoading || !webinar || isTracksLoading || !tracks) {
        return <LoaderPage />;
    }

    const mutationError = addOrUpdateTrackMutation.error || deleteTrackMutation.error;
    return (
        <ContainerNoTopSpace>
            <div>
                <PageHeader>
                    <PageHeaderContent>
                        {webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}
                    </PageHeaderContent>
                </PageHeader>

                <div className="px:4 md:px-6 pt-4 md:pt-10 max-w-4xl">
                    <BackToOriginLink fallback="content" />
                    <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                        {t('EditSubtitles.PageHeading')}
                    </Heading>
                    {mutationError && <ErrorMessage apiError={mutationError} />}
                    <div className="border-2 border-primary rounded-lg mt-8 mb-4 md:mb-6 p-4">
                        <p className="mb-4">{t('EditSubtitles.GeneratorSubtitles.InfoText')}</p>
                        {generatorStatus && !showSubtitlesGeneratorOptions && (
                            <>
                                {generatorStatus.isGenerating ? (
                                    <p>
                                        {t('EditSubtitles.GeneratorStatus.Generating')}
                                        <LoaderDots classNames="relative bottom-[-.17em] h-0.75em inline-block fill-current ms-1" />
                                    </p>
                                ) : (
                                    <Button variant="primary" onClick={() => setShowSubtitlesGeneratorOptions(true)}>
                                        {t('EditSubtitles.Button.GeneratorSubtitles')}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                    {showSubtitlesGeneratorOptions && <GenerateSubtitlesOptionsOrganism onClose={() => closeSubtitlesGeneratorOptions()} />}
                    <div className="max-w-4xl">
                        <div
                            {...getRootProps()}
                            className={`group relative w-full min-h-64 ${isDragActive && 'bg-accent-lighter'} hover:bg-accent-lighter focus-visible:bg-accent-lighter border-2 border-dashed ${!isDragActive && 'border-gray-500'} ${isDragActive && 'border-accent'} hover:border-accent focus-visible:border-accent rounded-lg flex mb-4 md:mb-6 outline-hidden overflow-hidden p-6 transition-colors duration-150`}
                        >
                            <div className="flex flex-col items-center m-auto">
                                <Upload classNames="size-12 mb-1 stroke-current group-hover:stroke-primary transition-color duration-200 ease-in-out" />
                                <p className="font-accent font-medium text-3xl mb-2">{isDragActive ? t('Generic.DragDrop.Active') : t('Generic.DragDrop')}</p>
                                <p>
                                    <Trans components={{ span: <span className="border-b-2 border-primary" /> }} i18nKey="Generic.DragDrop.BrowseFiles" />
                                </p>
                                {rejectionCode ? (
                                    <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />
                                ) : (
                                    <p className="mt-4">
                                        {t('Generic.DragDrop.AcceptedText')}
                                        <span className="font-medium">{t('EditSubtitles.DragDrop.AcceptedFileTypes')}</span>
                                    </p>
                                )}
                            </div>
                            <input {...getInputProps()} />
                        </div>
                    </div>
                    {tracks.length > 0 && (
                        <div className="bg-white rounded-lg shadow-md">
                            <div className="table-responsive">
                                <table className="table table-striped mt-2">
                                    <thead>
                                        <tr>
                                            <th className="w-4/5">{t('EditSubtitles.TableHeader.TrackId')}</th>
                                            <th>{t('EditSubtitles.TableHeader.Default')}</th>
                                            <th>{t('EditSubtitles.TableHeader.Enabled')}</th>
                                            <th className="w-30"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tracks.map((track) => (
                                            <tr key={track.trackId}>
                                                <td>{track.trackId}</td>
                                                <td>
                                                    <div className="pl-10">
                                                        <input
                                                            type="checkbox"
                                                            role="switch"
                                                            id="enabled"
                                                            checked={track.isDefault}
                                                            onChange={() => toggleTrackDefault(track.trackId)}
                                                            disabled={addOrUpdateTrackMutation.isPending || deleteTrackMutation.isPending}
                                                            className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                                                        />
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="pl-10">
                                                        <input
                                                            type="checkbox"
                                                            role="switch"
                                                            id="enabled"
                                                            checked={track.isEnabled}
                                                            onChange={() => toggleTrackEnable(track.trackId)}
                                                            disabled={addOrUpdateTrackMutation.isPending || deleteTrackMutation.isPending}
                                                            className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap">
                                                    <Button
                                                        onClick={() => onDownloadTrack(track.trackId)}
                                                        iconbtn
                                                        classNames="-my-4 translate-y-1.5"
                                                        isLoading={addOrUpdateTrackMutation.isPending || deleteTrackMutation.isPending}
                                                    >
                                                        <Download classNames="size-6 stroke-current" />
                                                        <span className="sr-only">
                                                            <Trans i18nKey="EditSubtitles.Button.Download" values={{ name: track.trackId }} />
                                                        </span>
                                                    </Button>
                                                    <Button
                                                        onClick={() => onDeleteTrack(track.trackId)}
                                                        iconbtn
                                                        classNames="-my-4 translate-y-1.5"
                                                        isLoading={addOrUpdateTrackMutation.isPending || deleteTrackMutation.isPending}
                                                    >
                                                        <Delete classNames="size-6 stroke-warning" />
                                                        <span className="sr-only">
                                                            <Trans i18nKey="EditSubtitles.Button.Delete" values={{ name: track.trackId }} />
                                                        </span>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </ContainerNoTopSpace>
    );
};
